import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";
import { showToast } from "../utils/toast";
import type { Room, RoomFeature, RoomFeatureAssignment } from "../types";
import { esc } from "../utils/html";
import { openCsvImportModal } from "../components/csv-import-modal";

/**
 * Räume der Schule mit ihrer Ausstattung.
 *
 * Die Ausstattung liegt bewusst nicht in festen Feldern. „Beamer ja/nein" trägt bei
 * Therapieberufen nicht: ein Physio-Praxisraum braucht acht Therapieliegen, ein
 * Ergo-Werkraum Werkbänke und eine Küchenzeile. Deshalb pflegt jede Schule ihren eigenen
 * Katalog, und ein Merkmal kann zählbar sein oder einfach nur vorhanden.
 *
 * Der Katalog steht unterhalb der Raumliste im selben Reiter. Ein eigener Reiter wäre
 * mehr Navigation für etwas, das man einmal einrichtet und danach kaum anfasst.
 */

let rooms: Room[] = [];
let catalog: RoomFeature[] = [];
let currentEl: HTMLElement | null = null;

/** Ausstattung eines Raums als kurze Zeile, z.B. „Beamer, Therapieliegen (8)". */
function featureSummary(features: RoomFeatureAssignment[]): string {
  if (features.length === 0) return "";
  return features
    .map(f => f.quantity === null ? f.name : `${f.name} (${f.quantity})`)
    .join(", ");
}

function renderRooms() {
  const tbody = currentEl?.querySelector("#raeume-body");
  if (!tbody) return;

  const cols = canWrite() ? 6 : 5;
  if (rooms.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${cols}" class="empty-state">Noch keine Räume angelegt</td></tr>`;
    return;
  }

  tbody.innerHTML = rooms.map((r, i) => `
    <tr${r.active ? "" : ` style="opacity:.55"`}>
      <td style="font-weight:500">
        ${esc(r.name)}
        ${r.active ? "" : `<span class="badge" style="margin-left:.5rem">Inaktiv</span>`}
      </td>
      <td class="text-muted">${esc(r.shortName || "")}</td>
      <td>${r.capacity === null ? `<span class="text-muted">nicht erfasst</span>` : `${r.capacity} Plätze`}</td>
      <td class="text-muted">${esc(featureSummary(r.features))}</td>
      <td class="text-muted">${r.lessonCount === 0 ? "" : `${r.lessonCount} ${r.lessonCount === 1 ? "Termin" : "Termine"}`}</td>
      ${writeOnly(`<td style="text-align:right;white-space:nowrap">
        <button class="btn btn-sm btn-secondary" data-edit-room="${i}">Bearbeiten</button>
        <button class="btn btn-sm btn-danger" data-del-room="${i}">Löschen</button>
      </td>`)}
    </tr>`).join("");
}

function renderCatalog() {
  const tbody = currentEl?.querySelector("#katalog-body");
  if (!tbody) return;

  const cols = canWrite() ? 3 : 2;
  if (catalog.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${cols}" class="empty-state">Noch keine Merkmale angelegt</td></tr>`;
    return;
  }

  tbody.innerHTML = catalog.map((f, i) => `
    <tr>
      <td style="font-weight:500">${esc(f.name)}</td>
      <td class="text-muted">${f.countable ? "mit Anzahl" : "vorhanden oder nicht"}</td>
      ${writeOnly(`<td style="text-align:right;white-space:nowrap">
        <button class="btn btn-sm btn-secondary" data-edit-feature="${i}">Bearbeiten</button>
        <button class="btn btn-sm btn-danger" data-del-feature="${i}">Löschen</button>
      </td>`)}
    </tr>`).join("");
}

// ── Raum-Formular ────────────────────────────────────────────────────

/** Zeile der Ausstattungsauswahl im Raum-Formular. */
function featureRow(f: RoomFeature, picked: RoomFeatureAssignment | undefined, disabled: string): string {
  const checked = picked ? " checked" : "";
  // Das Mengenfeld steht nur bei zählbaren Merkmalen und bleibt gesperrt, solange das
  // Merkmal nicht angehakt ist. Sonst tippt jemand eine Zahl in etwas Nichtgewähltes.
  const quantity = f.countable
    ? `<input type="number" min="1" step="1" class="feature-qty" data-qty="${f.id}"
             value="${picked?.quantity ?? ""}" placeholder="Anzahl"
             style="width:6rem;margin-left:.5rem"${picked ? "" : " disabled"}${disabled} />`
    : "";
  return `
    <label style="display:flex;align-items:center;gap:.5rem;padding:.3rem 0">
      <input type="checkbox" data-feature="${f.id}"${checked}${disabled} />
      <span style="flex:1">${esc(f.name)}</span>
      ${quantity}
    </label>`;
}

function openRoomForm(index?: number) {
  const isEdit = index !== undefined;
  const room = isEdit ? rooms[index] : null;
  const readOnly = !canWrite();
  const disabled = readOnly ? " disabled" : "";
  const picked = new Map((room?.features || []).map(f => [f.featureId, f]));

  openModal(isEdit ? "Raum bearbeiten" : "Neuer Raum", `
    <div class="form-grid">
      <div class="form-group">
        <label>Bezeichnung *</label>
        <input id="ra-name" value="${esc(room?.name || "")}"${disabled} placeholder="z.B. Raum A101" />
      </div>
      <div class="form-group">
        <label>Kürzel</label>
        <input id="ra-short" value="${esc(room?.shortName || "")}"${disabled} placeholder="z.B. A101" />
      </div>
    </div>
    <div class="form-grid">
      <div class="form-group">
        <label>Plätze</label>
        <input type="number" min="1" step="1" id="ra-capacity" value="${room?.capacity ?? ""}"${disabled} placeholder="z.B. 24" />
        <small class="text-muted">Leer lassen, wenn die Platzzahl keine Rolle spielt.</small>
      </div>
      <div class="form-group" style="display:flex;align-items:flex-end">
        <label style="display:flex;align-items:center;gap:.5rem">
          <input type="checkbox" id="ra-active"${room === null || room.active ? " checked" : ""}${disabled} />
          Aktiv
        </label>
      </div>
    </div>
    <div class="form-group">
      <label>Notiz</label>
      <input id="ra-note" value="${esc(room?.note || "")}"${disabled} />
    </div>
    <div class="form-group">
      <label>Ausstattung</label>
      ${catalog.length === 0
        ? `<p class="text-muted">Es sind noch keine Merkmale angelegt. Du kannst sie unterhalb der Raumliste ergänzen.</p>`
        : `<div style="max-height:16rem;overflow-y:auto;border:1px solid var(--gray-300);border-radius:.375rem;padding:.5rem .75rem">
             ${catalog.map(f => featureRow(f, picked.get(f.id), disabled)).join("")}
           </div>`}
    </div>
    <div class="alert alert-error hidden" id="ra-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="ra-cancel">${readOnly ? "Schließen" : "Abbrechen"}</button>
      ${writeOnly(`<button class="btn btn-primary" id="ra-save">Speichern</button>`)}
    </div>`, { wide: true });

  const mb = modalBody();
  mb.querySelector("#ra-cancel")!.addEventListener("click", closeModal);
  if (readOnly) { lockInputs(mb); return; }

  // Das Mengenfeld folgt dem Haken: abwählen leert es, damit keine verwaiste Zahl bleibt.
  mb.querySelectorAll<HTMLInputElement>("[data-feature]").forEach(box => {
    box.addEventListener("change", () => {
      const qty = mb.querySelector<HTMLInputElement>(`[data-qty="${box.dataset.feature}"]`);
      if (!qty) return;
      qty.disabled = !box.checked;
      if (!box.checked) qty.value = "";
    });
  });

  const showError = (message: string) => {
    const box = mb.querySelector("#ra-error") as HTMLElement;
    box.textContent = message;
    box.classList.remove("hidden");
  };

  mb.querySelector("#ra-save")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#ra-name") as HTMLInputElement).value.trim();
    if (!name) { showError("Bezeichnung ist ein Pflichtfeld"); return; }

    const rawCapacity = (mb.querySelector("#ra-capacity") as HTMLInputElement).value.trim();
    const capacity = rawCapacity === "" ? null : Number(rawCapacity);
    if (capacity !== null && (!Number.isInteger(capacity) || capacity < 1)) {
      showError("Die Platzzahl muss eine ganze Zahl über null sein");
      return;
    }

    const features: { featureId: number; quantity: number | null }[] = [];
    for (const box of mb.querySelectorAll<HTMLInputElement>("[data-feature]")) {
      if (!box.checked) continue;
      const featureId = Number(box.dataset.feature);
      const qtyField = mb.querySelector<HTMLInputElement>(`[data-qty="${featureId}"]`);
      const raw = qtyField?.value.trim() || "";
      const quantity = raw === "" ? null : Number(raw);
      if (quantity !== null && (!Number.isInteger(quantity) || quantity < 1)) {
        showError("Die Anzahl muss eine ganze Zahl über null sein");
        return;
      }
      features.push({ featureId, quantity });
    }

    const payload = {
      name,
      shortName: (mb.querySelector("#ra-short") as HTMLInputElement).value.trim() || null,
      capacity,
      note: (mb.querySelector("#ra-note") as HTMLInputElement).value.trim() || null,
      active: (mb.querySelector("#ra-active") as HTMLInputElement).checked,
      features,
    };

    const saveBtn = mb.querySelector("#ra-save") as HTMLButtonElement;
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      if (isEdit) await api.stundenplan.rooms.update(rooms[index].id, payload);
      else await api.stundenplan.rooms.create(payload);
      rooms = await api.stundenplan.rooms.list();
      renderRooms();
      closeModal();
      showToast(isEdit ? "Raum gespeichert" : "Raum angelegt");
    } catch (err: any) {
      showError(err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
  });

  (mb.querySelector("#ra-name") as HTMLInputElement).focus();
}

// ── Katalog-Formular ─────────────────────────────────────────────────

function openFeatureForm(index?: number) {
  const isEdit = index !== undefined;
  const feature = isEdit ? catalog[index] : null;

  openModal(isEdit ? "Merkmal bearbeiten" : "Neues Merkmal", `
    <div class="form-group">
      <label>Bezeichnung *</label>
      <input id="rf-name" value="${esc(feature?.name || "")}" placeholder="z.B. Therapieliegen" />
    </div>
    <div class="form-group">
      <label style="display:flex;align-items:center;gap:.5rem">
        <input type="checkbox" id="rf-countable"${feature?.countable ? " checked" : ""} />
        Mit Anzahl erfassen
      </label>
      <small class="text-muted">
        Anhaken, wenn die Menge zählt, etwa bei Therapieliegen oder Werkbänken. Für einen
        Beamer genügt „vorhanden".
      </small>
    </div>
    <div class="alert alert-error hidden" id="rf-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="rf-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="rf-save">Speichern</button>
    </div>`, { wide: false });

  const mb = modalBody();
  mb.querySelector("#rf-cancel")!.addEventListener("click", closeModal);

  const showError = (message: string) => {
    const box = mb.querySelector("#rf-error") as HTMLElement;
    box.textContent = message;
    box.classList.remove("hidden");
  };

  mb.querySelector("#rf-save")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#rf-name") as HTMLInputElement).value.trim();
    if (!name) { showError("Bezeichnung ist ein Pflichtfeld"); return; }
    const payload = {
      name,
      countable: (mb.querySelector("#rf-countable") as HTMLInputElement).checked,
      sortOrder: isEdit ? catalog[index].sortOrder : catalog.length + 1,
    };

    const saveBtn = mb.querySelector("#rf-save") as HTMLButtonElement;
    saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
    try {
      if (isEdit) await api.stundenplan.roomFeatures.update(catalog[index].id, payload);
      else await api.stundenplan.roomFeatures.create(payload);
      // Die Räume kommen mit: eine Umbenennung wirkt sich auf ihre Ausstattungszeile aus.
      [catalog, rooms] = await Promise.all([
        api.stundenplan.roomFeatures.list(),
        api.stundenplan.rooms.list(),
      ]);
      renderCatalog();
      renderRooms();
      closeModal();
      showToast(isEdit ? "Merkmal gespeichert" : "Merkmal angelegt");
    } catch (err: any) {
      showError(err.message);
      saveBtn.disabled = false; saveBtn.textContent = "Speichern";
    }
  });

  (mb.querySelector("#rf-name") as HTMLInputElement).focus();
}

export async function render(el: HTMLElement) {
  currentEl = el;
  el.innerHTML = `
    <div class="settings-page-header">
      <div>
        <h1>Räume</h1>
        <p>Die Räume dieser Schule mit ihrer Platzzahl und Ausstattung. Der Stundenplan erkennt damit Doppelbelegungen zuverlässig und kann einen freien Raum vorschlagen, der zur Gruppengröße passt.</p>
      </div>
      ${writeOnly(`<div class="settings-page-actions">
        <button class="btn btn-secondary" id="btn-csv-room">CSV-Import</button>
        <button class="btn btn-primary" id="btn-add-room">+ Neuer Raum</button>
      </div>`)}
    </div>
    ${readOnlyNotice()}
    <div class="card table-wrap">
      <table>
        <thead><tr>
          <th>Bezeichnung</th>
          <th style="width:110px">Kürzel</th>
          <th style="width:130px">Plätze</th>
          <th>Ausstattung</th>
          <th style="width:110px">Belegt</th>
          ${writeOnly(`<th style="width:200px;text-align:right">Aktionen</th>`)}
        </tr></thead>
        <tbody id="raeume-body"></tbody>
      </table>
    </div>

    <div class="settings-page-header" style="margin-top:2rem">
      <div>
        <h2 style="margin:0">Ausstattungskatalog</h2>
        <p>Woraus die Ausstattung eines Raums gewählt werden kann. Der Katalog ist eine Startliste und gehört dir: ergänze, was ihr habt, und lösche, was ihr nicht braucht.</p>
      </div>
      ${writeOnly(`<button class="btn btn-secondary" id="btn-add-feature">+ Neues Merkmal</button>`)}
    </div>
    <div class="card table-wrap">
      <table>
        <thead><tr>
          <th>Bezeichnung</th>
          <th style="width:220px">Erfassung</th>
          ${writeOnly(`<th style="width:200px;text-align:right">Aktionen</th>`)}
        </tr></thead>
        <tbody id="katalog-body"></tbody>
      </table>
    </div>`;

  try {
    // Der Katalog legt beim ersten Aufruf die Startliste an, deshalb zuerst.
    catalog = await api.stundenplan.roomFeatures.list();
    rooms = await api.stundenplan.rooms.list();
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }
  renderRooms();
  renderCatalog();

  el.querySelector("#btn-add-room")?.addEventListener("click", () => openRoomForm());
  el.querySelector("#btn-csv-room")?.addEventListener("click", () => openCsvImportModal({
    title: "Räume importieren",
    duplicateHint: "Räume mit gleicher Bezeichnung werden übersprungen. Die Ausstattung pflegst du danach am Raum.",
    fieldDescriptions: [
      { field: "Bezeichnung", hint: "Pflichtfeld, zum Beispiel „Raum S 1.04\"" },
      { field: "Kürzel", hint: "Optional, Beschriftung im Wochenraster" },
      { field: "Plätze", hint: "Optional, ganze Zahl" },
      { field: "Notiz", hint: "Optional" },
    ],
    onDownloadTemplate: () => api.stundenplan.rooms.csvTemplate(),
    onImport: (file) => api.stundenplan.rooms.csvImport(file),
    onComplete: async () => { rooms = await api.stundenplan.rooms.list(); renderRooms(); },
  }));
  el.querySelector("#btn-add-feature")?.addEventListener("click", () => openFeatureForm());

  el.querySelector("#raeume-body")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    const edit = target.closest("[data-edit-room]") as HTMLElement | null;
    if (edit) { openRoomForm(+edit.dataset.editRoom!); return; }

    const del = target.closest("[data-del-room]") as HTMLElement | null;
    if (!del) return;
    const room = rooms[+del.dataset.delRoom!];
    // lessons.room_id hängt an ON DELETE SET NULL: die Termine bleiben, sie verlieren nur
    // ihre Raumangabe. Das muss in der Rückfrage stehen, sonst klingt es nach Datenverlust.
    const ok = await confirmDialog({
      title: "Raum löschen",
      message: room.lessonCount > 0
        ? `„${room.name}" wird gelöscht. ${room.lessonCount} ${room.lessonCount === 1 ? "Termin verliert" : "Termine verlieren"} dadurch die Raumangabe, der Unterricht bleibt bestehen.`
        : `„${room.name}" wird gelöscht.`,
      confirmLabel: "Löschen", danger: true,
    });
    if (!ok) return;
    try {
      await api.stundenplan.rooms.delete(room.id);
      rooms = await api.stundenplan.rooms.list();
      renderRooms();
      showToast("Raum gelöscht");
    } catch (err: any) { showToast("Fehler: " + err.message); }
  });

  el.querySelector("#katalog-body")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    const edit = target.closest("[data-edit-feature]") as HTMLElement | null;
    if (edit) { openFeatureForm(+edit.dataset.editFeature!); return; }

    const del = target.closest("[data-del-feature]") as HTMLElement | null;
    if (!del) return;
    const feature = catalog[+del.dataset.delFeature!];
    const used = rooms.filter(r => r.features.some(f => f.featureId === feature.id)).length;
    const ok = await confirmDialog({
      title: "Merkmal löschen",
      message: used > 0
        ? `„${feature.name}" wird aus dem Katalog und aus ${used} ${used === 1 ? "Raum" : "Räumen"} entfernt.`
        : `„${feature.name}" wird aus dem Katalog entfernt.`,
      confirmLabel: "Löschen", danger: true,
    });
    if (!ok) return;
    try {
      await api.stundenplan.roomFeatures.delete(feature.id);
      [catalog, rooms] = await Promise.all([
        api.stundenplan.roomFeatures.list(),
        api.stundenplan.rooms.list(),
      ]);
      renderCatalog();
      renderRooms();
      showToast("Merkmal gelöscht");
    } catch (err: any) { showToast("Fehler: " + err.message); }
  });
}
